export type NormalizedImageMimeType = 'image/webp' | 'image/jpeg'

export type NormalizedImage = {
  blob: Blob
  width: number
  height: number
  byteSize: number
  mimeType: NormalizedImageMimeType
  hash: string
}

type ImagePreset = {
  maxEdge: number
  maxBytes: number
  minEdge: number
  square: boolean
}

const CAPTURE_PRESET: ImagePreset = { maxEdge: 2048, maxBytes: 700 * 1024, minEdge: 960, square: false }
const PROFILE_PRESET: ImagePreset = { maxEdge: 512, maxBytes: 180 * 1024, minEdge: 256, square: true }
const MAX_SOURCE_BYTES = 25 * 1024 * 1024
const IMAGE_QUALITIES = [0.82, 0.74, 0.66]

export const fitImageDimensions = (
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } => {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }
  const scale = maxEdge / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

const loadImage = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const sourceUrl = URL.createObjectURL(file)
  const image = new Image()
  image.onload = () => {
    URL.revokeObjectURL(sourceUrl)
    resolve(image)
  }
  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl)
    reject(new Error('Tydy could not read that image.'))
  }
  image.src = sourceUrl
})

const canvasBlob = (canvas: HTMLCanvasElement, mimeType: NormalizedImageMimeType, quality: number) => (
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, quality))
)

export const encodeCanvasImage = async (canvas: HTMLCanvasElement, quality: number): Promise<Blob> => {
  const webp = await canvasBlob(canvas, 'image/webp', quality)
  if (webp?.type === 'image/webp') return webp

  const jpeg = await canvasBlob(canvas, 'image/jpeg', quality)
  if (jpeg?.type === 'image/jpeg') return jpeg

  throw new Error('This browser could not prepare a supported image.')
}

const drawImage = (image: HTMLImageElement, width: number, height: number, square: boolean) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Tydy could not prepare that image.')

  if (!square) {
    context.drawImage(image, 0, 0, width, height)
    return canvas
  }

  const sourceSize = Math.min(image.naturalWidth, image.naturalHeight)
  const sourceX = Math.max(0, (image.naturalWidth - sourceSize) / 2)
  const sourceY = Math.max(0, (image.naturalHeight - sourceSize) / 2)
  context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, width, height)
  return canvas
}

const sha256 = async (blob: Blob) => {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 24)
}

const normalizeImage = async (file: File, preset: ImagePreset): Promise<NormalizedImage> => {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.')
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That image is too large to prepare safely.')

  const image = await loadImage(file)
  if (!image.naturalWidth || !image.naturalHeight) throw new Error('That image has no readable dimensions.')

  let dimensions = preset.square
    ? { width: Math.min(preset.maxEdge, image.naturalWidth, image.naturalHeight), height: Math.min(preset.maxEdge, image.naturalWidth, image.naturalHeight) }
    : fitImageDimensions(image.naturalWidth, image.naturalHeight, preset.maxEdge)
  let best: Blob | null = null

  for (let resizePass = 0; resizePass < 4; resizePass += 1) {
    const canvas = drawImage(image, dimensions.width, dimensions.height, preset.square)
    for (const quality of IMAGE_QUALITIES) {
      best = await encodeCanvasImage(canvas, quality)
      if (best.size <= preset.maxBytes) break
    }
    if (best.size <= preset.maxBytes || Math.max(dimensions.width, dimensions.height) <= preset.minEdge) break
    const nextLongEdge = Math.max(
      preset.minEdge,
      Math.round(Math.max(dimensions.width, dimensions.height) * 0.78),
    )
    dimensions = preset.square
      ? { width: nextLongEdge, height: nextLongEdge }
      : fitImageDimensions(image.naturalWidth, image.naturalHeight, nextLongEdge)
  }

  if (!best || best.size > preset.maxBytes) {
    throw new Error('Tydy could not reduce that image enough to store it responsibly.')
  }

  return {
    blob: best,
    width: dimensions.width,
    height: dimensions.height,
    byteSize: best.size,
    mimeType: best.type as NormalizedImageMimeType,
    hash: await sha256(best),
  }
}

export const imageProcessingService = {
  normalizeCaptureImage: (file: File) => normalizeImage(file, CAPTURE_PRESET),
  normalizeProfileImage: (file: File) => normalizeImage(file, PROFILE_PRESET),
}
